import { Alert, Button, Card, cn, EmptyState, formatDateTime, PageHeader, PageLoader, Tabs } from '@alicerce/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import type { NotificationRow } from '@/components/notification-bell';
import { errorMessage } from '@/lib/api';
import { useApi, useCurrent } from '@/lib/session';

const FILTERS = [
  { key: 'unread', label: 'Não lidas' },
  { key: 'all', label: 'Todas' },
] as const;

export function NotificationsPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { readOnly } = useCurrent();
  const [params, setParams] = useSearchParams();
  const filter = params.get('filtro') === 'todas' ? 'all' : 'unread';
  const q = useQuery({
    queryKey: ['notifications', filter],
    queryFn: () => api.get<NotificationRow[]>('/v1/core/notifications', { query: { unread: filter === 'unread' ? 'true' : undefined, limit: 100 } }),
  });
  const markRead = useMutation({
    mutationFn: (ids: string[] | 'all') => api.post('/v1/core/notifications/read', { ids }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const unread = q.data?.filter((n) => !n.readAt) ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notificações"
        description="Avisos do seu negócio. Escolha por onde receber em Configurações."
        actions={
          !readOnly &&
          unread.length > 0 && (
            <Button variant="secondary" loading={markRead.isPending} onClick={() => markRead.mutate('all')}>
              Marcar todas como lidas
            </Button>
          )
        }
      />
      <Tabs items={[...FILTERS]} value={filter} onChange={(k) => setParams(k === 'all' ? { filtro: 'todas' } : {})} />
      {markRead.isError && <Alert tone="danger">{errorMessage(markRead.error)}</Alert>}
      {q.isPending ? (
        <PageLoader />
      ) : q.isError ? (
        <Alert tone="danger">{errorMessage(q.error)}</Alert>
      ) : q.data.length === 0 ? (
        <EmptyState title={filter === 'unread' ? 'Tudo lido por aqui.' : 'Nenhuma notificação ainda.'} />
      ) : (
        <Card>
          <ul className="divide-y divide-line">
            {q.data.map((n) => (
              <li key={n.id} className={cn('flex items-start gap-3 px-5 py-4', !n.readAt && 'bg-brand-50/40')}>
                <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-brand-600')} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {n.link ? (
                      <Link to={n.link} className="hover:underline" onClick={() => !n.readAt && !readOnly && markRead.mutate([n.id])}>
                        {n.title}
                      </Link>
                    ) : (
                      n.title
                    )}
                    {!n.readAt && <span className="sr-only"> (não lida)</span>}
                  </p>
                  <p className="text-sm text-muted">{n.body}</p>
                  <p className="mt-1 text-xs text-muted">{formatDateTime(n.createdAt)}</p>
                </div>
                {!n.readAt && !readOnly && (
                  <Button size="sm" variant="ghost" onClick={() => markRead.mutate([n.id])}>
                    Marcar como lida
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
